import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import PreviousIcon from "../../assets/icons/previous.svg";
import NextIcon from "../../assets/icons/next.svg";
import "./CalendarModal.less";

type SelectedDate = {
  day?: number;
  month: number;
  year: number;
};

type CalendarModalProps = {
  title: string;
  onClose: () => void;
  onDateSelect?: (date: SelectedDate) => void;
  minAge?: number;
  disablePastDates?: boolean;
};

const CalendarModal: React.FC<CalendarModalProps> = ({ title, onClose, onDateSelect, minAge, disablePastDates = false }) => {
  const { t } = useTranslation();

  const getDefaultDate = () => {
    const today = new Date();
    if (minAge) {
      return new Date(today.getFullYear() - minAge, today.getMonth(), today.getDate());
    }
    return today;
  };

  const defaultDate = getDefaultDate();
  const [currentDate, setCurrentDate] = useState<Date>(() => getDefaultDate());
  const [selectedDate, setSelectedDate] = useState<SelectedDate | null>(null);
  const [showYearSelector, setShowYearSelector] = useState(false);
  const [showMonthSelector, setShowMonthSelector] = useState(false);

  useEffect(() => {
    setSelectedDate({
      day: defaultDate.getDate(),
      month: defaultDate.getMonth(),
      year: defaultDate.getFullYear(),
    });
  }, []);

  const currentYear = useMemo(() => currentDate.getFullYear(), [currentDate]);
  const currentMonth = useMemo(() => currentDate.getMonth(), [currentDate]);

  const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number): number => {
    return new Date(year, month, 1).getDay();
  };

  const monthTranslationKeys = useMemo(
    () => ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"],
    []
  );

  const daysOfWeekTranslationKeys = useMemo(
    () => ["sunday_short", "monday_short", "tuesday_short", "wednesday_short", "thursday_short", "friday_short", "saturday_short"],
    []
  );

  const generateYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let i = 1940; i <= currentYear; i++) {
      years.push(i);
    }
    return years.reverse();
  }, []);

  const handleYearSelect = (year: number) => {
    setCurrentDate((prev) => new Date(year, prev.getMonth(), 1));
    if (selectedDate) {
      setSelectedDate({ ...selectedDate, year });
    }
    setShowYearSelector(false);
  };

  const handleMonthSelect = (monthIndex: number) => {
    setCurrentDate((prev) => new Date(prev.getFullYear(), monthIndex, 1));
    if (selectedDate) {
      setSelectedDate({ ...selectedDate, month: monthIndex });
    }
    setShowMonthSelector(false);
  };

  const goToPreviousMonth = () => {
    setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const isPastDate = (day: number, month: number, year: number): boolean => {
    if (!disablePastDates) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(year, month, day);
    selectedDate.setHours(0, 0, 0, 0);
    return selectedDate < today;
  };

  const handleDateSelect = (day: number) => {
    if (isPastDate(day, currentMonth, currentYear)) {
      return;
    }
    const newSelectedDate: SelectedDate = {
      day,
      month: currentMonth,
      year: currentYear,
    };
    setSelectedDate(newSelectedDate);
  };

  const handleFinish = () => {
    if (selectedDate && onDateSelect) {
      onDateSelect(selectedDate);
    }
    onClose();
  };

  const capitalize = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const renderYearSelector = () => {
    return (
      <div className="year-selector">
        <div className="year-selector-header">
          <div className="year-selector-title">{t("select-year")}</div>
          <div className="year-selector-close" onClick={() => setShowYearSelector(false)}>
            &times;
          </div>
        </div>
        <div className="year-selector-grid">
          {generateYears.map((year) => (
            <div key={year} className={`year-selector-item ${year === currentYear ? "selected" : ""}`} onClick={() => handleYearSelect(year)}>
              {year}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderMonthSelector = () => {
    return (
      <div className="month-selector">
        <div className="month-selector-header">
          <div className="month-selector-title">{t("select-month")}</div>
          <div className="month-selector-close" onClick={() => setShowMonthSelector(false)}>
            &times;
          </div>
        </div>
        <div className="month-selector-grid">
          {monthTranslationKeys.map((month, index) => (
            <div key={month} className={`month-selector-item ${index === currentMonth ? "selected" : ""}`} onClick={() => handleMonthSelect(index)}>
              {capitalize(t(month))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDayOfMonth = getFirstDayOfMonth(currentYear, currentMonth);
    const days = [];

    const dayLabels = (
      <div key="week-header" className="calendar-days-row calendar-header-row">
        {daysOfWeekTranslationKeys.map((day, index) => (
          <div key={`label-${index}`} className="calendar-day-label">
            {capitalize(t(day))}
          </div>
        ))}
      </div>
    );
    days.push(dayLabels);

    let dayCounter = 1;
    const maxWeeks = 6;

    for (let i = 0; i < maxWeeks; i++) {
      const week = [];
      let hasDays = false;

      for (let j = 0; j < 7; j++) {
        if ((i === 0 && j < firstDayOfMonth) || dayCounter > daysInMonth) {
          week.push(<div key={`empty-${i}-${j}`} className="calendar-day empty"></div>);
        } else {
          const dayNumber = dayCounter;
          const isSelected = selectedDate?.day === dayNumber && selectedDate?.month === currentMonth && selectedDate?.year === currentYear;
          const isPast = isPastDate(dayNumber, currentMonth, currentYear);

          week.push(
            <div
              key={`day-${i}-${j}`}
              className={`calendar-day ${isSelected ? "selected" : ""} ${isPast ? "disabled" : ""}`}
              onClick={() => handleDateSelect(dayNumber)}
            >
              {dayNumber}
            </div>
          );
          dayCounter++;
          hasDays = true;
        }
      }

      if (dayCounter <= daysInMonth || i === 0 || hasDays) {
        days.push(
          <div key={`week-${i}`} className="calendar-days-row">
            {week}
          </div>
        );
      }
    }
    return <div className="calendar-grid">{days}</div>;
  };

  return (
    <div className="calendar-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="calendar-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-line-wrapper" onClick={onClose}>
          <div className="modal-header-line"></div>
        </div>
        <div className="calendar-modal-container">
          <div className="calendar-modal-title">{title}</div>

          <div className="calendar-container">
            <div className="calendar-header">
              <img src={PreviousIcon} className="calendar-header-icon" alt="previous" onClick={goToPreviousMonth} />
              <p
                className="calendar-header-text clickable"
                onClick={() => {
                  setShowMonthSelector(true);
                  setShowYearSelector(false);
                }}
              >
                {capitalize(t(monthTranslationKeys[currentMonth]))}
              </p>
              <p
                className="calendar-header-text clickable"
                onClick={() => {
                  setShowYearSelector(true);
                  setShowMonthSelector(false);
                }}
              >
                {currentYear}
              </p>
              <img src={NextIcon} className="calendar-header-icon" alt="next" onClick={goToNextMonth} />
            </div>

            {showYearSelector && renderYearSelector()}
            {showMonthSelector && renderMonthSelector()}

            {!showYearSelector && !showMonthSelector && <div className="calendar-date-container">{renderCalendar()}</div>}
          </div>

          <button className={`calendar-modal-terminate-button ${!selectedDate ? "disabled" : ""}`} onClick={handleFinish} disabled={!selectedDate}>
            {t("confirm")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalendarModal;
